import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, Loader2 } from "lucide-react";

export default function PhoneRequiredModal() {
  const { isAuthenticated, isCustomer } = useAuth();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAuthenticated || !isCustomer) return;

    const needsPhone = localStorage.getItem("nc_needs_phone");
    if (needsPhone !== "true") return;

    // ALWAYS verify with backend — never trust localStorage alone
    // If phone is already saved in DB, clear the flag and don't show popup
    api.get("/auth/me/phone-check")
      .then(r => {
        if (r.data.has_phone) {
          // Phone already exists — clear the stale flag, never show popup
          localStorage.removeItem("nc_needs_phone");
        } else {
          // Phone truly missing — show popup
          setOpen(true);
        }
      })
      .catch(() => {
        // On API error, don't annoy user with popup
        localStorage.removeItem("nc_needs_phone");
      });
  }, [isAuthenticated, isCustomer]);

  const handleSubmit = async () => {
    const clean = phone.replace(/\s/g, "").replace("+91", "").replace("-", "");
    if (clean.length < 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const fullPhone = clean.startsWith("91") ? `+${clean}` : `+91${clean}`;
      await api.post("/auth/me/update-phone", { phone: fullPhone });
      localStorage.removeItem("nc_needs_phone");
      setOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save phone number");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="bg-white border-0 max-w-sm p-0 overflow-hidden rounded-2xl shadow-2xl [&>button]:hidden" data-testid="phone-required-modal">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 px-6 py-6 text-white text-center">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
            <Phone className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="text-xl font-bold mb-1">Add Your Mobile Number</DialogTitle>
          <p className="text-green-200 text-xs">Required for order updates & delivery notifications</p>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="phone-error">{error}</p>}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">WhatsApp / Mobile Number</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">+91</span>
              <Input
                data-testid="phone-required-input"
                placeholder=""
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9\s]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                className="pl-14 h-12 rounded-xl border-2 border-gray-200 focus:border-green-500 text-base"
              />
            </div>
            <p className="text-xs text-gray-400">We'll use this for order confirmations, shipping updates & OTP verification</p>
          </div>

          <Button
            data-testid="phone-required-submit"
            className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold"
            onClick={handleSubmit}
            disabled={loading || !phone.trim()}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : "Save & Continue"}
          </Button>

          <p className="text-[10px] text-center text-gray-400">This is a one-time step. Your number will be kept secure.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
